import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import axios from 'axios';
import { colors } from '../../style/rulesEngine';
import { getAuthHeaders, getBaseUrl } from '../../helpers/rulesApiHelpers';

/**
 * TagPickerModal — single-select tag picker matching the spec §8.4 Add Tag behaviour.
 * Mirrors the TagManager selectionMode="single" contract described in v6-frontend.md.
 *
 * Props:
 *   visible         - boolean
 *   onClose         - () => void
 *   title           - string (modal header)
 *   selectedTagId   - number | null  (currently selected tag)
 *   onTagSelected   - (tagId, tagName) => void
 */
const TagPickerModal = ({ visible, onClose, title = 'Select Tag to Add', selectedTagId, onTagSelected }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Fetch tags when modal opens
  useEffect(() => {
    if (visible) {
      setSearch('');
      setShowCreate(false);
      setNewTagName('');
      setCreateError(null);
      fetchTags();
    }
  }, [visible]);

  const fetchTags = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      const res = await axios.get(`${url}/order_tags.json`, { headers });
      const raw = Array.isArray(res.data) ? res.data : res.data?.tags || [];
      setTags(raw.map((t) => ({ id: t.id, name: t.name || t.tag_name || '' })));
    } catch (err) {
      console.warn('[TagPickerModal] fetch failed:', err.message);
      setFetchError('Could not load tags. Please try again.');
    }
    setLoading(false);
  };

  const handleSelect = (tag) => {
    onTagSelected(tag.id, tag.name);
    onClose();
  };

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const url = await getBaseUrl();
      const headers = await getAuthHeaders();
      const res = await axios.post(
        `${url}/order_tags/create_or_update`,
        { order_id: { name: newTagName.trim() } },
        { headers }
      );
      const newTag = res.data;
      const tagId = newTag.id;
      const tagName = newTag.name || newTag.tag_name || newTagName.trim();
      onTagSelected(tagId, tagName);
      onClose();
    } catch (err) {
      setCreateError('Failed to create tag. Please try again.');
      console.warn('[TagPickerModal] create failed:', err.message);
    }
    setCreating(false);
  };

  const filtered = tags.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' }}>
          <TouchableWithoutFeedback>
            <View style={{
              width: '90%',
              maxWidth: 400,
              maxHeight: '75%',
              borderRadius: 12,
              overflow: 'hidden',
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.15)',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.5,
              shadowRadius: 16,
              elevation: 24,
            }}>
              <LinearGradient colors={['#142131', '#1e3248']}>
                {/* Header */}
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingHorizontal: 16,
                  paddingVertical: 14,
                  borderBottomWidth: 1,
                  borderBottomColor: 'rgba(255,255,255,0.1)',
                }}>
                  <Text style={{ fontFamily: 'Poppins_600SemiBold', fontSize: 15, color: '#fff' }}>
                    {title}
                  </Text>
                  <TouchableOpacity onPress={onClose} style={{ padding: 4 }}>
                    <AntDesign name="close" size={18} color={colors.textMuted} />
                  </TouchableOpacity>
                </View>

                {/* Search input */}
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderBottomWidth: 1,
                  borderBottomColor: 'rgba(255,255,255,0.08)',
                  paddingHorizontal: 14,
                  paddingVertical: 8,
                  gap: 8,
                }}>
                  <AntDesign name="search1" size={14} color={colors.textMuted} />
                  <TextInput
                    style={{
                      flex: 1,
                      fontFamily: 'Poppins_500Medium',
                      fontSize: 13,
                      color: '#fff',
                      paddingVertical: 4,
                    }}
                    value={search}
                    onChangeText={setSearch}
                    placeholder="Search tags..."
                    placeholderTextColor={colors.textMuted}
                    autoFocus
                  />
                  {search.length > 0 && (
                    <TouchableOpacity onPress={() => setSearch('')}>
                      <AntDesign name="close" size={12} color={colors.textMuted} />
                    </TouchableOpacity>
                  )}
                </View>

                {/* Tag list */}
                <ScrollView style={{ maxHeight: 280 }} bounces={false} keyboardShouldPersistTaps="handled">
                  {loading ? (
                    <View style={{ alignItems: 'center', paddingVertical: 24 }}>
                      <ActivityIndicator size="small" color={colors.accent} />
                    </View>
                  ) : fetchError ? (
                    <View style={{ alignItems: 'center', paddingVertical: 20, paddingHorizontal: 16, gap: 10 }}>
                      <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 13, color: colors.danger, textAlign: 'center' }}>
                        {fetchError}
                      </Text>
                      <TouchableOpacity onPress={fetchTags} style={{ paddingVertical: 6, paddingHorizontal: 16, backgroundColor: colors.primary, borderRadius: 6 }}>
                        <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 13, color: '#fff' }}>Retry</Text>
                      </TouchableOpacity>
                    </View>
                  ) : filtered.length === 0 ? (
                    <View style={{ alignItems: 'center', paddingVertical: 20 }}>
                      <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 13, color: colors.textMuted }}>
                        {search ? 'No tags match your search.' : 'No tags yet.'}
                      </Text>
                    </View>
                  ) : (
                    filtered.map((tag) => {
                      const isSelected = tag.id === selectedTagId;
                      return (
                        <TouchableOpacity
                          key={tag.id}
                          onPress={() => handleSelect(tag)}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 11,
                            paddingHorizontal: 16,
                            borderBottomWidth: 1,
                            borderBottomColor: 'rgba(255,255,255,0.05)',
                            backgroundColor: isSelected ? 'rgba(28,187,119,0.12)' : 'transparent',
                          }}
                        >
                          <Text style={{
                            flex: 1,
                            fontFamily: 'Poppins_500Medium',
                            fontSize: 13,
                            color: isSelected ? colors.accent : '#fff',
                          }}>
                            {tag.name}
                          </Text>
                          {isSelected && (
                            <AntDesign name="check" size={14} color={colors.accent} />
                          )}
                        </TouchableOpacity>
                      );
                    })
                  )}
                </ScrollView>

                {/* Create new tag section */}
                <View style={{ borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)', padding: 12 }}>
                  {showCreate ? (
                    <View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <TextInput
                          style={{
                            flex: 1,
                            fontFamily: 'Poppins_500Medium',
                            fontSize: 13,
                            color: '#fff',
                            borderBottomWidth: 1.5,
                            borderBottomColor: colors.accent,
                            paddingVertical: 4,
                          }}
                          value={newTagName}
                          onChangeText={(v) => { setNewTagName(v); setCreateError(null); }}
                          placeholder="New tag name"
                          placeholderTextColor={colors.textMuted}
                          autoFocus
                        />
                        {creating ? (
                          <ActivityIndicator size="small" color={colors.accent} />
                        ) : (
                          <TouchableOpacity onPress={handleCreate} style={{ padding: 4 }}>
                            <AntDesign name="check" size={18} color={colors.accent} />
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity onPress={() => { setShowCreate(false); setNewTagName(''); setCreateError(null); }} style={{ padding: 4 }}>
                          <AntDesign name="close" size={16} color={colors.textMuted} />
                        </TouchableOpacity>
                      </View>
                      {createError && (
                        <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 11, color: colors.danger }}>
                          {createError}
                        </Text>
                      )}
                    </View>
                  ) : (
                    <TouchableOpacity
                      onPress={() => setShowCreate(true)}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
                    >
                      <AntDesign name="pluscircleo" size={14} color={colors.accent} />
                      <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 13, color: colors.accent }}>
                        Create new tag
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </LinearGradient>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default TagPickerModal;
